import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/context/auth-context';
import { ApiError } from '@/lib/queryClient';
import { apiRequest } from '@/lib/queryClient';

export interface FeatureAccess {
  fullMonitoring: boolean;
  fullScanner: boolean;
  dataExport: boolean;
}

export interface SubscriptionInfo {
  tier: string;
  active: boolean;
  expiresAt: Date | null;
  features: FeatureAccess;
}

interface SubscriptionContextType {
  subscription: SubscriptionInfo | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading, authToken, refreshAuthToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = async () => {
    try {
      // Use apiRequest so we send Authorization bearer token in addition to cookies
      const response = await apiRequest('GET', '/api/user/subscription');
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      // Attempt a single token refresh and retry on 401
      if (error instanceof ApiError && error.status === 401 && typeof refreshAuthToken === 'function') {
        try {
          const newToken = await refreshAuthToken();
          if (newToken) {
            const retryResponse = await apiRequest('GET', '/api/user/subscription');
            const retryData = await retryResponse.json();
            setSubscription(retryData);
            return;
          }
        } catch (refreshErr) {
          console.error('Token refresh failed for subscription fetch:', refreshErr);
        }
      }
      console.error('Failed to fetch subscription info:', error);
      // Default to free tier on error
      setSubscription({
        tier: 'free tier',
        active: false,
        expiresAt: null,
        features: {
          fullMonitoring: false,
          fullScanner: false,
          dataExport: false,
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch subscription after auth is complete and user is available
    if (!authLoading && user && authToken) {
      fetchSubscription();
    } else if (!authLoading && !user) {
      // If not authenticated, set to default free tier
      setSubscription({
        tier: 'free tier',
        active: false,
        expiresAt: null,
        features: {
          fullMonitoring: false,
          fullScanner: false,
          dataExport: false,
        }
      });
      setLoading(false);
    }
  }, [authLoading, user]);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      loading,
      refetch: fetchSubscription
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Utility hook for feature access
export const useFeatureAccess = () => {
  const { subscription } = useSubscription();
  return subscription?.features || {
    fullMonitoring: false,
    fullScanner: false,
    dataExport: false,
  };
};
